'use client'

import { ReactNode } from 'react'
import { useLoanComparatorStore } from '@/app/stores/loan/loan-comparator.store'
import { ILoanQuote } from '@/app/models/loanQuote'
import { PROJECT_TYPE_NEW_LOAN, PROJECT_TYPE_SWITCHING } from '@/app/constants/loanTypes'
import DescriptiveToggle, { DescriptiveToggleOption } from '@/app/components/atomic/molecules/DescriptiveToggle'
import LoanFormField from '@/app/components/loanComparator/fields/LoanFormField'
import ShadowBox from '@/app/components/atomic/ShadowBox'
import AppIcon from '@/app/components/atomic/AppIcon'

export type ProjectPathValue = 'new' | 'switching'
export type BorrowersValue = 'alone' | 'duo'

const pathOptions: DescriptiveToggleOption<ProjectPathValue>[] = [
    {
        value: 'new',
        title: 'Nouvelle assurance',
        description: 'Vous assurez votre prêt dès le départ au meilleur tarif.',
        icon: <AppIcon iconClassName="fa-house" size="lg" type="regular" className="text-pale" />,
    },
    {
        value: 'switching',
        title: "Changer d'assurance",
        description: 'Vous résiliez votre contrat actuel pour une offre plus avantageuse.',
        icon: <AppIcon iconClassName="fa-arrows-rotate" size="lg" type="regular" className="text-pale" />,
    },
]

const borrowersOptions: DescriptiveToggleOption<BorrowersValue>[] = [
    {
        value: 'alone',
        title: 'Seul',
        description: "J'assure ma part du prêt",
        icon: <AppIcon iconClassName="fa-user" size="lg" type="regular" className="text-pale" />,
    },
    {
        value: 'duo',
        title: 'À deux',
        description: 'Nous assurons les deux profils',
        icon: <AppIcon iconClassName="fa-user-group" size="lg" type="regular" className="text-pale" />,
    },
]

export const buildPathQuoteUpdate = (path: ProjectPathValue): Partial<ILoanQuote> => {
    if (path === 'new') {
        const nextMonth = new Date()
        nextMonth.setMonth(nextMonth.getMonth() + 1)
        const formattedDate = `${String(nextMonth.getDate()).padStart(2, '0')}/${String(
            nextMonth.getMonth() + 1
        ).padStart(2, '0')}/${nextMonth.getFullYear()}`

        return {
            project_progression: PROJECT_TYPE_NEW_LOAN.toString(),
            is_loan_repurchase: false,
            date_contract_begin: formattedDate,
        }
    }

    return {
        project_progression: PROJECT_TYPE_SWITCHING.toString(),
        is_loan_repurchase: false,
    }
}

export const deriveProjectPathValue = (quote: ILoanQuote | null): ProjectPathValue | null => {
    // project_progression n'existe que côté client (écrit au clic) ; la vérité serveur est type_quote
    // Un quote legacy « rachat de crédit » retombe sur « new » (le choix n'existe plus)
    if (quote?.project_progression) {
        if (String(quote.project_progression) === String(PROJECT_TYPE_SWITCHING)) return 'switching'
        return 'new'
    }
    if (quote?.type_quote) {
        if (quote.type_quote !== 'newLoan') return 'switching'
        return 'new'
    }
    return null
}

export const deriveBorrowersValue = (quote: ILoanQuote | null): BorrowersValue | null => {
    if (quote?.co_borrower?.activated === true) return 'duo'
    if (quote?.co_borrower?.activated === false) return 'alone'
    return null
}

interface LoanComparatorProjectStepProps {
    error: string | null
    onAnswerChange: () => void
    // The screen stays mounted while hidden: the parent only passes the footer on the active screen
    footer: ReactNode
}

export default function LoanComparatorProjectStep({
    error,
    onAnswerChange,
    footer,
}: LoanComparatorProjectStepProps) {
    const quote = useLoanComparatorStore((state) => state.quote)
    const quoteActions = useLoanComparatorStore((state) => state.quoteActions)
    const borrowerActions = useLoanComparatorStore((state) => state.borrower)

    const pathValue = deriveProjectPathValue(quote)
    const borrowersValue = deriveBorrowersValue(quote)

    // Mêmes side-effects que selectNewLoanPath / selectSwitchingPath / selectLoanRepurchasePath du parcours
    const handlePathChange = (path: ProjectPathValue) => {
        if (!quote || path === pathValue) return
        onAnswerChange()
        quoteActions.update(buildPathQuoteUpdate(path))
    }

    const handleBorrowersChange = (value: BorrowersValue) => {
        if (!quote || value === borrowersValue) return
        onAnswerChange()

        if (value === 'duo') {
            borrowerActions.addCoBorrower()
        } else {
            borrowerActions.deactivateCoBorrower()
        }
    }


    return (
        <section>
            <ShadowBox flatOnMobile className="mt-6 mb-5 md:py-5 md:px-5">
                <h2
                    style={{ fontFamily: 'var(--font-recoleta)' }}
                    className="block text-3xl md:text-4xl font-bold text-pale mt-0 mb-4 md:mb-8"
                >
                    Quel est votre projet ?<span className="text-red-600 md:hidden">*</span>
                </h2>

                <div className="flex flex-col gap-8">
                    <LoanFormField
                        label="Vous souhaitez assurer un nouveau prêt immobilier ou renégocier votre assurance actuelle ?*"
                        labelClassName="!text-base inline-block mb-4 md:mb-5"
                        inline={false}
                        error={error && !pathValue ? { message: error } : undefined}
                    >
                        <DescriptiveToggle
                            options={pathOptions}
                            value={pathValue}
                            onChange={handlePathChange}
                            columns={2}
                            ariaLabel="Vous souhaitez assurer un nouveau prêt immobilier ou renégocier votre assurance actuelle ?"
                        />
                    </LoanFormField>

                    <LoanFormField
                        label="Votre emprunt est-il souscrit seul ou conjointement avec un co-emprunteur ?*"
                        labelClassName="!text-base inline-block mb-4 md:mb-5"
                        inline={false}
                        error={error && !borrowersValue ? { message: error } : undefined}
                    >
                        <DescriptiveToggle
                            options={borrowersOptions}
                            value={borrowersValue}
                            onChange={handleBorrowersChange}
                            columns={2}
                            mobileColumns={2}
                            ariaLabel="Votre emprunt est-il souscrit seul ou conjointement avec un co-emprunteur ?"
                        />
                    </LoanFormField>
                </div>

                {footer && <div className="mt-4 md:mt-8">{footer}</div>}
            </ShadowBox>
        </section>
    )
}
